import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useGuildRights, type EntryKey } from '@/lib/access';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Ban, UserX, Clock, MessageSquare, ShieldPlus, Trash2, Lock, Unlock, Timer } from 'lucide-react';
import { ChannelPicker, CHANNEL_TYPE } from '@/components/ChannelPicker';
import { UserPicker } from '@/components/UserPicker';

/**
 * Klasse-C actions page. Everything here posts to /api/actions/*, which
 * fire-and-forgets to the bot via Redis. Result toasts arrive via the
 * live-events stream (LiveEventsToast) automatically.
 */
type ActionKind =
  | 'moderation'
  | 'channel'
  | 'message'
  | 'modmail'
  | 'role'
  | 'ticket';

// Each action belongs to a plugin entry (lib/access.ts) — only actions the
// user may edit are offered; the API enforces the same rights.
const ACTIONS: { key: ActionKind; label: string; entry: EntryKey }[] = [
  { key: 'moderation', label: 'Moderation (Ban / Kick / Timeout)', entry: 'moderation' },
  { key: 'channel', label: 'Channel-Aktionen (Löschen / Sperren / Slowmode)', entry: 'moderation' },
  { key: 'message', label: 'Nachricht senden', entry: 'actions' },
  { key: 'modmail', label: 'ModMail-Antwort (DM)', entry: 'modMail' },
  { key: 'role', label: 'Rolle geben / entfernen', entry: 'moderation' },
  { key: 'ticket', label: 'Ticket-Antwort', entry: 'tickets' },
];

export function ServerActions() {
  const { guildId } = useParams<{ guildId: string }>();
  const [selected, setSelected] = useState<ActionKind>('moderation');
  const { can } = useGuildRights(guildId);
  if (!guildId) return null;
  const allowed = ACTIONS.filter((a) => can(a.entry, 'edit'));
  const current = allowed.some((a) => a.key === selected) ? selected : allowed[0]?.key;

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <h1 className="text-2xl font-semibold">Aktionen</h1>
      <p className="text-sm text-muted-foreground">
        Wähle eine Aktion — sie läuft asynchron, das Ergebnis erscheint als Toast
        unten rechts sobald der Bot bestätigt hat.
      </p>

      {allowed.length === 0 && (
        <p className="rounded-md border bg-muted/40 p-3 text-sm text-muted-foreground">
          Für diesen Server sind dir keine Aktionen freigeschaltet.
        </p>
      )}

      {allowed.length > 0 && (
      <div>
        <Label htmlFor="action-select">Aktion</Label>
        <select
          id="action-select"
          value={current}
          onChange={(e) => setSelected(e.target.value as ActionKind)}
          className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
        >
          {allowed.map((a) => (
            <option key={a.key} value={a.key}>
              {a.label}
            </option>
          ))}
        </select>
      </div>
      )}

      {current === 'moderation' && <ModerationCard guildId={guildId} />}
      {current === 'channel' && <ChannelActionsCard guildId={guildId} />}
      {current === 'message' && <MessageCard guildId={guildId} />}
      {current === 'modmail' && <ModmailSendCard guildId={guildId} />}
      {current === 'role' && <RoleCard guildId={guildId} />}
      {current === 'ticket' && <TicketReplyCard />}
    </div>
  );
}

// ---------------------------- ModmailSend ----------------------------

function ModmailSendCard({ guildId }: { guildId: string }) {
  const [userId, setUserId] = useState<string | null>(null);
  const [message, setMessage] = useState('');
  const sendMut = useMutation({
    mutationFn: () =>
      api('/api/actions/modmail-send', {
        method: 'POST',
        body: JSON.stringify({ guildId, userId, message }),
      }),
    onSuccess: () => setMessage(''),
  });
  const canSubmit = !!userId && message.trim().length > 0 && !sendMut.isPending;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <MessageSquare className="h-4 w-4 text-primary" /> ModMail-Antwort
        </CardTitle>
        <CardDescription>
          Schickt einem User eine DM als „Antwort vom Moderator" — z.B. um auf eine
          ModMail-Nachricht (siehe Tickets/Channel) zurückzumelden.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <Label>Empfänger</Label>
          <UserPicker guildId={guildId} value={userId} onChange={setUserId} />
        </div>
        <div>
          <Label htmlFor="mm-msg">Nachricht</Label>
          <Textarea emoji
            id="mm-msg"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            className="min-h-[80px]"
            maxLength={2000}
          />
          <div className="mt-1 text-right text-xs text-muted-foreground">
            {message.length} / 2000
          </div>
        </div>
        <div className="flex items-center justify-end gap-2">
          {sendMut.isSuccess && <span className="text-sm text-success">Gesendet ✓</span>}
          {sendMut.error && (
            <span className="text-sm text-destructive">
              {(sendMut.error as Error).message}
            </span>
          )}
          <Button disabled={!canSubmit} onClick={() => sendMut.mutate()}>
            {sendMut.isPending ? 'Sende…' : 'Senden'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------- Channel Actions ----------------------------

function ChannelActionsCard({ guildId }: { guildId: string }) {
  const [channelId, setChannelId] = useState<string | null>(null);
  const [amount, setAmount] = useState(50);
  const [slowmodeSec, setSlowmodeSec] = useState(0);

  const useAct = (path: string) =>
    useMutation({
      mutationFn: (body: object) =>
        api(`/api/actions/${path}`, { method: 'POST', body: JSON.stringify(body) }),
    });
  const clearMut = useAct('channel-clear');
  const lockMut = useAct('channel-lock');
  const unlockMut = useAct('channel-unlock');
  const slowmodeMut = useAct('channel-slowmode');

  const disabled = !channelId;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Channel-Aktionen</CardTitle>
        <CardDescription>
          Nachrichten löschen, Channel sperren/entsperren, Slowmode setzen.
          Bulk-Delete kann nur Nachrichten löschen, die höchstens 14 Tage alt sind.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label>Channel</Label>
          <ChannelPicker
            guildId={guildId}
            value={channelId}
            onChange={setChannelId}
            types={[CHANNEL_TYPE.GUILD_TEXT, CHANNEL_TYPE.GUILD_ANNOUNCEMENT]}
          />
        </div>

        <div className="flex flex-wrap items-end gap-3 rounded-md border p-3">
          <div className="w-32">
            <Label>Anzahl</Label>
            <Input
              type="number"
              min={2}
              max={100}
              value={amount}
              onChange={(e) =>
                setAmount(Math.min(100, Math.max(2, Number(e.target.value) || 2)))
              }
            />
          </div>
          <Button
            variant="destructive"
            disabled={disabled || clearMut.isPending}
            onClick={() => {
              if (
                confirm(`${amount} Nachrichten im gewählten Channel wirklich löschen?`)
              ) {
                clearMut.mutate({ guildId, channelId, amount });
              }
            }}
          >
            <Trash2 className="mr-1 h-4 w-4" />
            {clearMut.isPending ? 'Lösche…' : 'Nachrichten löschen'}
          </Button>
        </div>

        <div className="flex flex-wrap gap-2 rounded-md border p-3">
          <Button
            variant="outline"
            disabled={disabled || lockMut.isPending}
            onClick={() => lockMut.mutate({ guildId, channelId })}
          >
            <Lock className="mr-1 h-4 w-4" />
            {lockMut.isPending ? 'Sperre…' : 'Channel sperren'}
          </Button>
          <Button
            variant="outline"
            disabled={disabled || unlockMut.isPending}
            onClick={() => unlockMut.mutate({ guildId, channelId })}
          >
            <Unlock className="mr-1 h-4 w-4" />
            {unlockMut.isPending ? 'Entsperre…' : 'Channel entsperren'}
          </Button>
        </div>

        <div className="flex flex-wrap items-end gap-3 rounded-md border p-3">
          <div className="w-40">
            <Label>Slowmode (Sekunden)</Label>
            <select
              value={slowmodeSec}
              onChange={(e) => setSlowmodeSec(Number(e.target.value))}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value={0}>Aus (0)</option>
              <option value={5}>5 s</option>
              <option value={10}>10 s</option>
              <option value={30}>30 s</option>
              <option value={60}>1 Min</option>
              <option value={300}>5 Min</option>
              <option value={600}>10 Min</option>
              <option value={3600}>1 Std</option>
              <option value={21600}>6 Std (Max)</option>
            </select>
          </div>
          <Button
            variant="outline"
            disabled={disabled || slowmodeMut.isPending}
            onClick={() => slowmodeMut.mutate({ guildId, channelId, seconds: slowmodeSec })}
          >
            <Timer className="mr-1 h-4 w-4" />
            {slowmodeMut.isPending ? 'Setze…' : 'Slowmode setzen'}
          </Button>
        </div>

        {(clearMut.error || lockMut.error || unlockMut.error || slowmodeMut.error) && (
          <p className="text-sm text-destructive">
            Fehler:{' '}
            {
              ((clearMut.error || lockMut.error || unlockMut.error || slowmodeMut.error) as Error)
                .message
            }
          </p>
        )}
      </CardContent>
    </Card>
  );
}

// ------------------------------ Moderation -------------------------------

function ModerationCard({ guildId }: { guildId: string }) {
  const [userId, setUserId] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [durationSec, setDurationSec] = useState(600);

  const useAct = (path: string) =>
    useMutation({
      mutationFn: (body: object) =>
        api(`/api/actions/${path}`, { method: 'POST', body: JSON.stringify(body) }),
    });
  const banMut = useAct('ban');
  const kickMut = useAct('kick');
  const timeoutMut = useAct('timeout');

  const commonBody = () => ({ guildId, userId, reason: reason || undefined });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Ban className="h-4 w-4 text-destructive" /> Moderation
        </CardTitle>
        <CardDescription>Ban, Kick oder Timeout eines Users.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <Label>User</Label>
          <UserPicker guildId={guildId} value={userId} onChange={setUserId} />
        </div>
        <div>
          <Label htmlFor="mod-reason">Grund (optional)</Label>
          <Input
            id="mod-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>
        <div>
          <Label htmlFor="mod-dur">Timeout-Dauer (Sekunden, nur bei Timeout)</Label>
          <Input
            id="mod-dur"
            type="number"
            min={0}
            max={28 * 24 * 60 * 60}
            value={durationSec}
            onChange={(e) => setDurationSec(Number(e.target.value))}
          />
        </div>
        <div className="flex flex-wrap gap-2 pt-2">
          <Button
            variant="destructive"
            disabled={!userId || banMut.isPending}
            onClick={() => banMut.mutate(commonBody())}
          >
            <Ban className="mr-1 h-4 w-4" /> Ban
          </Button>
          <Button
            variant="destructive"
            disabled={!userId || kickMut.isPending}
            onClick={() => kickMut.mutate(commonBody())}
          >
            <UserX className="mr-1 h-4 w-4" /> Kick
          </Button>
          <Button
            variant="outline"
            disabled={!userId || timeoutMut.isPending}
            onClick={() =>
              timeoutMut.mutate({ ...commonBody(), durationSec })
            }
          >
            <Clock className="mr-1 h-4 w-4" />
            {durationSec === 0 ? 'Untimeout' : 'Timeout'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ------------------------------ Send Message -----------------------------

function MessageCard({ guildId }: { guildId: string }) {
  const [channelId, setChannelId] = useState('');
  const [content, setContent] = useState('');
  const [silent, setSilent] = useState(false);

  const mut = useMutation({
    mutationFn: () =>
      api('/api/actions/send-message', {
        method: 'POST',
        body: JSON.stringify({ guildId, channelId, content, ...(silent ? { silent: true } : {}) }),
      }),
    onSuccess: () => setContent(''),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <MessageSquare className="h-4 w-4 text-primary" /> Nachricht senden
        </CardTitle>
        <CardDescription>Sendet eine Nachricht als Bot in einen Channel.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <Label htmlFor="msg-cid">Channel ID</Label>
          <Input
            id="msg-cid"
            value={channelId}
            onChange={(e) => setChannelId(e.target.value)}
            placeholder="123456789012345678"
            pattern="\d+"
          />
        </div>
        <div>
          <Label htmlFor="msg-content">Text</Label>
          <Textarea emoji
            id="msg-content"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            maxLength={2000}
            rows={4}
          />
          <div className="mt-1 flex items-center justify-between gap-3 text-xs text-muted-foreground">
            <Switch
              checked={silent}
              onChange={(e) => setSilent(e.target.checked)}
              label="Leise senden (niemand wird gepingt)"
              className="text-xs"
            />
            <span>{content.length}/2000</span>
          </div>
        </div>
        <Button
          disabled={!channelId || !content || mut.isPending}
          onClick={() => mut.mutate()}
        >
          Senden
        </Button>
      </CardContent>
    </Card>
  );
}

// ------------------------------ Roles ------------------------------------

function RoleCard({ guildId }: { guildId: string }) {
  const [userId, setUserId] = useState('');
  const [roleId, setRoleId] = useState('');

  const useOp = (op: 'add' | 'remove') =>
    useMutation({
      mutationFn: () =>
        api('/api/actions/assign-role', {
          method: 'POST',
          body: JSON.stringify({ guildId, userId, roleId, op }),
        }),
    });
  const addMut = useOp('add');
  const removeMut = useOp('remove');

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <ShieldPlus className="h-4 w-4 text-primary" /> Rollen
        </CardTitle>
        <CardDescription>Rolle einem User geben oder wegnehmen.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <Label htmlFor="role-uid">User ID</Label>
          <Input
            id="role-uid"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            pattern="\d+"
          />
        </div>
        <div>
          <Label htmlFor="role-rid">Role ID</Label>
          <Input
            id="role-rid"
            value={roleId}
            onChange={(e) => setRoleId(e.target.value)}
            pattern="\d+"
          />
        </div>
        <div className="flex gap-2 pt-2">
          <Button
            disabled={!userId || !roleId || addMut.isPending}
            onClick={() => addMut.mutate()}
          >
            Rolle geben
          </Button>
          <Button
            variant="outline"
            disabled={!userId || !roleId || removeMut.isPending}
            onClick={() => removeMut.mutate()}
          >
            Rolle entfernen
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ------------------------------ Ticket Reply -----------------------------

function TicketReplyCard() {
  const [ticketId, setTicketId] = useState('');
  const [content, setContent] = useState('');
  const [silent, setSilent] = useState(false);

  const mut = useMutation({
    mutationFn: () =>
      api('/api/actions/ticket-reply', {
        method: 'POST',
        body: JSON.stringify({ ticketId, content, ...(silent ? { silent: true } : {}) }),
      }),
    onSuccess: () => setContent(''),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <MessageSquare className="h-4 w-4 text-primary" /> Ticket-Antwort
        </CardTitle>
        <CardDescription>
          Antwortet in einem offenen Ticket. Nachricht wird als Bot geschrieben mit
          Hinweis wer sie geschickt hat, und im Ticket-Verlauf gespeichert.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <Label htmlFor="tr-id">Ticket ID (cuid)</Label>
          <Input
            id="tr-id"
            value={ticketId}
            onChange={(e) => setTicketId(e.target.value)}
            placeholder="clxxxxxxxxxxxxx"
          />
        </div>
        <div>
          <Label htmlFor="tr-content">Antwort</Label>
          <Textarea emoji
            id="tr-content"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            maxLength={2000}
            rows={4}
          />
          <div className="mt-1 flex items-center justify-between gap-3 text-xs text-muted-foreground">
            <Switch
              checked={silent}
              onChange={(e) => setSilent(e.target.checked)}
              label="Leise senden (niemand wird gepingt)"
              className="text-xs"
            />
            <span>{content.length}/2000</span>
          </div>
        </div>
        <Button
          disabled={!ticketId || !content || mut.isPending}
          onClick={() => mut.mutate()}
        >
          Antwort senden
        </Button>
      </CardContent>
    </Card>
  );
}
